import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import usePage from '../../hooks/usePage'
import { FlexColumn, FlexRow } from '../../components/atoms/layout/Flex'
import { CompanyActions } from '../../store/company'
import { CompanySelectors } from '../../store/company/selectors'
import { CompanyRepresentative } from '../../types/Company'
import useDay from '../../hooks/useDay'
import Label from '../../components/atoms/display/Label'
import Button from '../../components/atoms/inputs/Button'
import PdfViewer from '../../components/atoms/display/PdfViewer'
import usePdf from '../../hooks/usePdf'
import Link from '../../components/atoms/navigation/Link'
import CompanyRepresentativeAcceptDialog from '../../components/organisms/admin/CompanyRepresentativeAcceptDialog'
import CompanyRepresentativeRejectDialog from '../../components/organisms/admin/CompanyRepresentativeRejectDialog'
import CompanyRepresentativeCertificationDeleteDialog from '../../components/organisms/admin/CompanyRepresentativeCertificationDeleteDialog'
import Image from '../../components/atoms/display/Image'

function status(representative: CompanyRepresentative) {
  if (representative.rejectedAt !== null) {
    return '却下済み'
  }
  if (representative.submittedAt === null) {
    return '本人確認書類提出待ち'
  }
  if (representative.acceptedAt !== null) {
    return '承認済み'
  }
  return '未承認'
}

export default function AdminRepresentativePage() {
  const { changeTitle } = usePage()
  const dispatch = useDispatch()
  const representative = useSelector(CompanySelectors.representative)
  const { formatDateTime } = useDay()
  const { representativeId } = useParams()
  const pdfParams = usePdf({})
  const [acceptDialog, setAcceptDialog] = useState(false)
  const [rejectDialog, setRejectDialog] = useState(false)
  const [certificationDeleteDialog, setCertificationDeleteDialog] = useState(false)

  useEffect(() => {
    changeTitle('管理', '代表の本人確認')
    if (representativeId) {
      dispatch(CompanyActions.fetchRepresentative({ id: representativeId }))
    }
  }, [changeTitle, dispatch, representativeId])

  if (representative === undefined) {
    return (
      <FlexColumn>
        <Label text="読込中" />
      </FlexColumn>
    )
  }

  const hasCertificationFiles = representative.certificationFiles.length > 0
  // 承認済みで書類が残っている代表者だけ、本人確認書類を削除できる
  const canDeleteCertificationFiles = representative.acceptedAt !== null && hasCertificationFiles

  return (
    <FlexColumn>
      <Link href="/admin/representatives">代表者一覧に戻る</Link>
      <FlexRow>
        <FlexRow width={96}>
          <Label text="企業名" />
        </FlexRow>
        <Label text={representative.company.name} />
      </FlexRow>
      <FlexRow>
        <FlexRow width={96}>
          <Label text="ユーザー名" />
        </FlexRow>
        <Label text={representative.user.name} />
      </FlexRow>
      <FlexRow>
        <FlexRow width={96}>
          <Label text="提出日時" />
        </FlexRow>
        <Label text={formatDateTime(representative.submittedAt)} />
      </FlexRow>
      <FlexRow align="center">
        <FlexRow width={96}>
          <Label text="ステータス" />
        </FlexRow>
        <Label text={status(representative)} />
        {representative.rejectedAt === null && representative.acceptedAt === null && (
          <>
            <Button height="small" onClick={() => setAcceptDialog(true)}>
              承認する
            </Button>
            <Button height="small" color="caution" onClick={() => setRejectDialog(true)}>
              差し戻す
            </Button>
            <CompanyRepresentativeAcceptDialog
              open={acceptDialog}
              representative={representative}
              onClose={() => setAcceptDialog(false)}
            />
            <CompanyRepresentativeRejectDialog
              open={rejectDialog}
              representative={representative}
              onClose={() => setRejectDialog(false)}
            />
          </>
        )}
      </FlexRow>
      <FlexColumn space={0}>
        <FlexRow align="center">
          <Label text="本人確認書類" />
          {canDeleteCertificationFiles && (
            <>
              <Button height="small" color="caution" onClick={() => setCertificationDeleteDialog(true)}>
                削除する
              </Button>
              <CompanyRepresentativeCertificationDeleteDialog
                open={certificationDeleteDialog}
                representative={representative}
                onClose={() => setCertificationDeleteDialog(false)}
              />
            </>
          )}
        </FlexRow>
        {!hasCertificationFiles && <Label text="本人確認書類はありません" />}
        {representative.certificationFiles.map((file) => (
          <>
            {file.type === 'pdf' ? (
              <PdfViewer url={file.url} httpHeaders={{}} params={pdfParams} defaultZoomLevel="pageWidth" />
            ) : (
              <Image url={file.url} alt="本人確認書類" />
            )}
          </>
        ))}
      </FlexColumn>
    </FlexColumn>
  )
}
